import { useCallback, useState } from "react";
import { defineMessages } from "react-intl";
import classNames from "classnames";
import without from "lodash/without";

import { useFormatMessage } from "../Intl/Context";
import { useFieldContext } from "../Form/Field";
import inputConnect from "./inputConnect";
import FileInputFilesList from "./FileInputFilesList";
import Icon from "../Utils/Icon";
import { useComponentIsUnmounted } from "../Utils/useComponentIsUnmounted";
import { useInputAriaDescribedBy } from "../Form/useInputAriaDescribedBy";
import { useFieldErrors } from "../Form/useFieldErrors";

const FileInput = ({ multiple = false, maxFiles: maxFilesProp = 1, ...props }) => {
  const { fieldName, files, onFileUploaded, updateValue, attachmentToken } = props;
  const maxFiles = maxFilesProp || (multiple ? 0 : 1);
  const maxFilesReached = maxFiles && files.length >= maxFiles;

  const [isInputFocused, setIsInputFocused] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const t = useFormatMessage();
  const selectOrDropFileLabel = t(intlMessages.selectOrDropFile);

  const { fieldId, isRequired, isInvalid } = useFieldContext();
  const ariaDescribedBy = useInputAriaDescribedBy();

  const isFilePresent = files.length > 0;

  const uploaderClassName = classNames("uploader", "row", {
    "uploader-dragging": dragOver,
    "uploader-file-added": isFilePresent,
    "uploader-empty": !isFilePresent,
    "max-files-reached": maxFilesReached
  });

  const isUnmounted = useComponentIsUnmounted();

  const { removeErrors } = useFieldErrors(fieldName);

  const addFile = useCallback(
    file => {
      // Clean previous errors display as, if there were errors before, the file is
      // already removed when errors are displayed. So we just clean the errors out when
      // a new file is selected.
      removeErrors();

      // We need to defer state update to avoid multiple files to only add the
      // last one.
      setTimeout(() => {
        if (isUnmounted() || maxFilesReached) return;

        updateValue({ files: [...files, file] });
      }, 0);
    },
    [files, isUnmounted, maxFilesReached, removeErrors, updateValue]
  );

  const addFiles = useCallback(
    files => {
      Array.from(files).forEach(file => addFile(file));
    },
    [addFile]
  );

  const handleFileDragOver = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();

      setDragOver(true);
    },
    [setDragOver]
  );

  const handleFileDragLeave = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();

      setDragOver(false);
    },
    [setDragOver]
  );

  const handleFileSelected = useCallback(
    e => {
      addFiles(e.target.files);
    },
    [addFiles]
  );

  const handleFileDropped = useCallback(
    e => {
      handleFileDragLeave(e);
      addFiles(e.dataTransfer.files);
    },
    [addFiles, handleFileDragLeave]
  );

  const handleUploadStarted = useCallback(() => {
    const newFiles = [...files];

    updateValue({ files: newFiles });
  }, [files, updateValue]);

  const handleUploadCompleted = useCallback(() => {
    const newFiles = [...files];

    updateValue({ files: newFiles });

    if (onFileUploaded) {
      onFileUploaded(newFiles);
    }
  }, [files, onFileUploaded, updateValue]);

  const handleRemove = useCallback(
    file => {
      const newFiles = without(files, file);

      updateValue({ files: newFiles });
    },
    [files, updateValue]
  );

  const handleInputFocused = useCallback(() => setIsInputFocused(true), []);
  const handleInputBlurred = useCallback(() => setIsInputFocused(false), []);

  const uploaderSelectZoneClassName = classNames(
    "uploader-select-zone",
    "padding-sm",
    "d-flex",
    "flex-column",
    "justify-content-center",
    { focused: isInputFocused }
  );

  return (
    <div
      className={uploaderClassName}
      onDragOver={handleFileDragOver}
      onDragLeave={handleFileDragLeave}
      onDrop={handleFileDropped}
    >
      <div className="col-md-8 order-lg-12">
        <ul className="uploader-files-list list-unstyled text-small">
          <FileInputFilesList
            inputFieldName={fieldName}
            files={files}
            onUploadStart={handleUploadStarted}
            onUploadComplete={handleUploadCompleted}
            attachmentToken={attachmentToken}
            onRemove={handleRemove}
          />
        </ul>
      </div>

      <div className="col-md-4">
        <div className={uploaderSelectZoneClassName}>
          <div className="uploader-file-field-container">
            <div className="uploader-file-field-label text-muted">
              <Icon className="fa fa-upload uploader-file-icon d-block margin-bottom-xs" />
              {selectOrDropFileLabel}
            </div>

            <input
              id={fieldId}
              type="file"
              className="uploader-file-field"
              onChange={handleFileSelected}
              onFocus={handleInputFocused}
              onBlur={handleInputBlurred}
              multiple={multiple}
              disabled={maxFilesReached}
              aria-required={isRequired}
              aria-invalid={isInvalid}
              aria-describedby={ariaDescribedBy}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default inputConnect(FileInput, { fields: { files: [] } });

const intlMessages = defineMessages({
  selectOrDropFile: {
    id: "inputs.files.selectOrDropFile",
    defaultMessage: "Sélectionnez ou glissez votre fichier"
  }
});
